/**
 * Static starter templates for the conversation-first interview wizard.
 *
 * These templates back two surfaces:
 * 1. The wizard's template-bubble step: when an admin picks a template,
 *    `getStarterFields` seeds the builder canvas with sensible starter cards.
 * 2. A silent fallback when the `offline-session` worker's `/forms/generate`
 *    endpoint fails or times out, the wizard still lands the admin in a
 *    useful starting state, and the SSE error gets captured to PostHog by
 *    the wizard component (out of scope for this module).
 *
 * Phase 1 invariants (enforced by `lib/__tests__/form-templates.test.ts`):
 * - Every starter field has `type: 'free_form_explore'`.
 * - Every starter field has `mustOrTry: 'try'` (toggle hidden in v1).
 * - Field `id`s and `name`s are unique within each template.
 *
 * @module lib/form-templates
 */

import type { FormField, FormFieldBase, FormFieldConfig } from '../types';

/**
 * The set of template keys the wizard exposes as bubble selectors. Extending
 * this is purely additive, add a new key + entry in `FORM_TEMPLATES`.
 */
export type FormTemplateKey =
  | 'feedback'
  | 'product_walkthrough'
  | 'customer_onboarding'
  | 'custom';

/**
 * Generate a stable, unique field identifier suitable for `FormField.id`.
 * Wraps `crypto.randomUUID()` so callers don't need to know about the global.
 *
 * @returns A new RFC 4122 v4 UUID.
 */
export function generateFieldId(): string {
  return crypto.randomUUID();
}

/**
 * Convert a human-readable label into a snake_case programmatic key suitable
 * for `FormField.name`. Shared by the wizard (`CreateInterviewForm`) and the
 * `offline-session` worker's `/forms/generate` handler so all surfaces derive
 * field names identically.
 *
 * Rules: lowercase, replace any run of non-alphanumeric chars with a single
 * underscore, trim leading/trailing underscores.
 *
 * @param label Admin-facing question text.
 * @returns Snake_case identifier (`/^[a-z][a-z0-9_]*$/` once a leading letter
 *          is present in the label).
 */
export function labelToName(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Starter card shape, `FormField` minus the runtime-generated `id`.
 *
 * Spelled as `Omit<FormFieldBase, 'id'> & FormFieldConfig` rather than
 * `Omit<FormField, 'id'>` because the latter collapses the
 * `FormFieldConfig` discriminated union (TS computes `Omit<A & B, K>`
 * via `Pick<keyof (A & B), …>`, which loses per-variant fields like
 * `topic` and `options`). The explicit form preserves narrowing.
 *
 * IDs are deliberately NOT baked into `FORM_TEMPLATES`. Module-load random
 * value generation (`crypto.randomUUID()`) is forbidden in the Cloudflare
 * Workers global scope, and the worker bundles this module to read
 * `OBJECTIVE_TEMPLATES`. `getStarterFields` is the only place that mints
 * IDs, it runs inside a request handler where random calls are allowed. */
export type StarterFormField = Omit<FormFieldBase, 'id'> & FormFieldConfig;

/**
 * Build a Free Form Explore starter field with Phase 1 defaults stamped:
 * `mustOrTry: 'try'`, `required: false`, `type: 'free_form_explore'`.
 *
 * Returns the field WITHOUT an `id`, `getStarterFields` mints one at call
 * time. See {@link StarterFormField} for the rationale.
 *
 * @param label Admin-facing question text.
 * @param topic One-sentence hint hollie uses to guide exploration.
 * @returns An ID-less FFE starter field.
 */
function ffe(label: string, topic: string): StarterFormField {
  return {
    name: labelToName(label),
    label,
    required: false,
    mustOrTry: 'try',
    type: 'free_form_explore',
    topic,
  };
}

/**
 * Curated FFE starter cards per template. All Phase 1 templates are
 * FFE-only, hollie's behaviour mirrors today's freeform interview, just
 * structured into discrete blocks the admin can edit.
 *
 * Stored without `id` so module evaluation has no side effects. IDs are
 * stamped by `getStarterFields` at call time (inside a request handler
 * for the worker, or in browser code for the wizard).
 */
export const FORM_TEMPLATES: Record<FormTemplateKey, StarterFormField[]> = {
  feedback: [
    ffe(
      'How would you describe your overall experience?',
      'Get a candid summary of how the respondent felt using the product.',
    ),
    ffe(
      'What stood out as the best moment?',
      'Surface the single most memorable positive moment in their experience.',
    ),
    ffe(
      'Where did you run into friction?',
      'Identify pain points, confusion, or anything that slowed them down.',
    ),
    ffe(
      'How does it compare to alternatives you have used?',
      'Understand competitive context and what made them switch or consider switching.',
    ),
    ffe(
      'Would you recommend it to a peer, and why?',
      'Probe willingness to recommend and the reasoning behind it.',
    ),
  ],

  product_walkthrough: [
    ffe(
      'What is the main use case you rely on the product for?',
      'Anchor the conversation in the respondent\'s primary workflow.',
    ),
    ffe(
      'Walk us through a typical session from start to finish.',
      'Capture a step-by-step picture of how they actually use the product.',
    ),
    ffe(
      'Which feature has become indispensable for you?',
      'Identify the highest-value feature in their day-to-day usage.',
    ),
    ffe(
      'What capability is missing that would make a real difference?',
      'Surface the most-wanted gap from someone with hands-on context.',
    ),
  ],

  customer_onboarding: [
    ffe(
      'What is your role and what are you responsible for?',
      'Establish role, scope, and decision-making power within the company.',
    ),
    ffe(
      'How big is your team or company?',
      'Capture company size to contextualise their needs and constraints.',
    ),
    ffe(
      'What is the primary problem you are hoping the product will solve?',
      'Pin down the headline outcome they are buying toward.',
    ),
    ffe(
      'How will you know it is working, what does success look like?',
      'Surface explicit success criteria so onboarding can target them.',
    ),
    ffe(
      'What tools are you using today for this, and what is not working?',
      'Map current tooling and the specific gaps motivating the switch.',
    ),
  ],

  custom: [
    ffe(
      'Tell us about your experience.',
      'Open-ended starter prompt, admin will customise this before saving.',
    ),
  ],
};

/**
 * Return a deep-cloned starter `FormField[]` for the given template. Cloning
 * is essential: `FORM_TEMPLATES` is a module-level constant and callers
 * routinely mutate the result (drag-reorder, edit label, add fields) before
 * saving. Without the clone, mutations would leak across wizard sessions.
 *
 * Each field gets a freshly minted `id` here, `FORM_TEMPLATES` entries are
 * ID-less by design (see {@link StarterFormField}). Generating the ID inside
 * this function (rather than at module load) is what keeps the worker bundle
 * compatible: random-value generation in the global scope is forbidden by
 * the Workers runtime; in handler scope it's fine.
 *
 * @param template Template key from the wizard's bubble selector.
 * @returns A fresh array of `FormField` objects safe to mutate.
 */
export function getStarterFields(template: FormTemplateKey): FormField[] {
  return FORM_TEMPLATES[template].map((field) => ({
    ...structuredClone(field),
    id: generateFieldId(),
  }));
}

// ============================================================================
// OBJECTIVE TEMPLATES, lean-context metadata for the LLM-driven wizard
// ============================================================================
//
// Two surfaces consume these templates:
//
//   1. /objectives/suggest, the worker sends brand research + the
//      `shortHint` of EACH template to Gemini, which returns 3-4
//      brand-tailored objective cards each tagged with a `templateKey`.
//      Only `shortHint`s travel, `formShape` is deliberately withheld
//      to keep the categorisation prompt lean.
//
//   2. /forms/generate, once the user picks a card (or types via the
//      escape hatch), the worker injects ONLY the selected template's
//      `formShape` into the conversation-generation prompt. Other templates'
//      shape data never enters that context.
//
// Splitting suggest-context from generate-context is what stops Gemini
// from carrying every template's prompt-shaping weight on every call.

/**
 * Per-template metadata for the LLM-driven wizard.
 *
 * `shortHint` is sent to the suggest-objectives prompt for every template
 * (small budget); `formShape` is sent to the generate-form prompt for the
 * SELECTED template only. Keeps each Gemini call's context lean.
 */
export interface ObjectiveTemplate {
  /** Stable identifier, matches {@link FormTemplateKey}. */
  key: FormTemplateKey;
  /**
   * One-line categorisation hint for the suggest-objectives prompt. Keep
   * concise (≲ 80 chars), every template's hint is concatenated into
   * that prompt. This is the only template metadata Gemini sees while
   * picking a card type.
   */
  shortHint: string;
  /**
   * Detailed prompt-shaping payload. Sent to generate-form ONLY for the
   * template the user selected, never to the suggest-objectives call,
   * and never bundled across templates.
   */
  formShape: {
    /** Voice/tone guidance for question authoring. */
    tone: string;
    /**
     * Concrete example field labels in this template's style. Anchors
     * Gemini's output without prescribing exact wording.
     */
    typicalFields: string[];
    /** Inclusive `[min, max]` target field count for the generated conversation. */
    fieldCountTarget: [number, number];
  };
}

/**
 * Lean-context metadata for every template. Keep `shortHint` short, it's
 * sent on every suggest-objectives call. `formShape` is only ever fetched
 * for the one template the user picks.
 */
export const OBJECTIVE_TEMPLATES: Record<FormTemplateKey, ObjectiveTemplate> = {
  feedback: {
    key: 'feedback',
    shortHint: 'Collect candid feedback on a product, feature, or experience.',
    formShape: {
      tone: 'Warm, neutral, encouraging. Invite candor without leading.',
      typicalFields: [
        'How would you describe your overall experience?',
        'What stood out as the best moment?',
        'Where did you run into friction?',
      ],
      fieldCountTarget: [4, 6],
    },
  },

  product_walkthrough: {
    key: 'product_walkthrough',
    shortHint: 'Capture how a customer actually uses the product day-to-day.',
    formShape: {
      tone: 'Curious, hands-on. Anchor in concrete sessions and real workflows.',
      typicalFields: [
        'Walk us through a typical session from start to finish.',
        'Which feature has become indispensable for you?',
        'What capability is missing that would change things?',
      ],
      fieldCountTarget: [4, 6],
    },
  },

  customer_onboarding: {
    key: 'customer_onboarding',
    shortHint: 'Onboard a new customer, capture role, problem, and success criteria.',
    formShape: {
      tone: 'Efficient, business-oriented. Get to outcomes quickly.',
      typicalFields: [
        'What is your role and what are you responsible for?',
        'What problem are you hoping the product will solve?',
        'How will you know it is working?',
      ],
      fieldCountTarget: [4, 6],
    },
  },

  custom: {
    key: 'custom',
    shortHint: 'Open-ended interview shaped entirely by the supplied objective.',
    formShape: {
      tone: 'Flexible. Match the objective\'s tone and depth.',
      typicalFields: [],
      fieldCountTarget: [3, 7],
    },
  },
};
